import { MessageSquare } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useConversationMessages } from '../hooks/use-conversation-messages';
import { useSlashCommands } from '../hooks/use-slash-commands';
import { Button } from './Button';
import { ChatInput } from './ChatInput';
import { MessageBubble } from './MessageBubble';
import { PlaybookSuggestions } from './PlaybookSuggestions';
import { Spinner } from './Spinner';
import { StreamRenderer, type StreamEvent } from './StreamRenderer';

import type { ChatInputHandle } from './ChatInput';

export interface ChatPanelProps {
  conversationId: string | null;
  conversationTitle?: string | undefined;
  className?: string | undefined;
  onStreamComplete?: ((conversationId: string) => void) | undefined;
  onMessageSent?: ((content: string) => void) | undefined;
  onTaskEvent?: ((event: TaskCreatedEvent | TaskStatusUpdateEvent) => void) | undefined;
  pendingMessage?: { message: string; files?: File[] } | null | undefined;
  onPendingMessageConsumed?: (() => void) | undefined;
}

export function ChatPanel({ conversationId, conversationTitle, onStreamComplete, onMessageSent, onTaskEvent, pendingMessage, onPendingMessageConsumed }: ChatPanelProps) {
  const { messages, status, sendMessage, isAgentTyping, isExtendedSession, retryLastMessage } = useConversationMessages(conversationId, { onStreamComplete });

  const handlePlanApproved = useCallback((planId: string) => {
    void sendMessage(`[SYSTEM_TRIGGER:plan_approved:${planId}]`);
  }, [sendMessage]);
  const { commands: slashCommands, status: slashCommandStatus, fetch: fetchSlashCommands } = useSlashCommands();
  const messagesRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputHandle>(null);
  const isUserScrolledUp = useRef(false);
  const [showNewMessages, setShowNewMessages] = useState(false);
  const handleScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const threshold = 100;
    isUserScrolledUp.current = el.scrollTop + el.clientHeight < el.scrollHeight - threshold;
    if (!isUserScrolledUp.current) setShowNewMessages(false);
  };

  // Auto-scroll: fires on every messages array change (including streaming content growth)
  useEffect(() => {
    if (!isUserScrolledUp.current && messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  // "New messages" pill: fires only when discrete new messages are added (not streaming growth)
  useEffect(() => {
    if (isUserScrolledUp.current && messages.length > 0) {
      setShowNewMessages(true);
    }
  }, [messages.length]);

  // Auto-send a pending message from WelcomeScreen on mount
  const pendingConsumedRef = useRef(false);
  useEffect(() => {
    if (pendingMessage && conversationId && !pendingConsumedRef.current) {
      pendingConsumedRef.current = true;
      void sendMessage(pendingMessage.message, pendingMessage.files);
      onPendingMessageConsumed?.();
    }
  }, [pendingMessage, conversationId, sendMessage, onPendingMessageConsumed]);


  const handleSend = useCallback(async (content: string, files?: File[]) => {
    onMessageSent?.(content);
    return sendMessage(content, files);
  }, [sendMessage, onMessageSent]);

  if (!conversationId) {
    return (
      <div data-testid="chat-empty-state" className="flex flex-col items-center justify-center h-full p-sd-4 text-center bg-sd-background">
        <div className="w-10 h-10 rounded-sd-lg bg-sd-primary-subtle flex items-center justify-center mb-sd-3 sd-reveal-1">
          <MessageSquare size={20} className="text-sd-primary" />
        </div>
        <p className="text-sd-lg font-semibold text-sd-text mb-sd-1 sd-reveal-2">Select a conversation</p>
        <p className="text-sd-sm text-sd-text-muted sd-reveal-3">Choose a conversation from the list to start chatting</p>
      </div>
    );
  }
  return (
    <div className="relative flex flex-col h-full min-h-0 bg-sd-background">
      {conversationTitle && (
        <div data-testid="chat-header" className="shrink-0 relative flex items-center gap-sd-2 px-sd-4 py-sd-3 bg-sd-surface shadow-[0_1px_3px_rgba(0,0,0,0.06)] z-[1]">
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-sd-warning/60 via-sd-warning/20 to-transparent" />
          <h3 className="text-sd-base font-semibold text-sd-text truncate tracking-[-0.01em]">{conversationTitle}</h3>
        </div>
      )}
      <div ref={messagesRef} onScroll={handleScroll} className="flex-1 overflow-y-auto overflow-x-hidden p-sd-3 space-y-sd-3">
        {status === 'loading' || status === 'idle' ? (
          <div data-testid="chat-loading" className="flex items-center justify-center h-full">
            <Spinner size="md" />
          </div>
        ) : messages.length === 0 ? (
          <div data-testid="chat-welcome" className="flex flex-col items-center justify-center h-full text-center px-sd-4">
            <div className="max-w-lg w-full">
              <h2 className="sd-reveal-1 text-[22px] font-bold text-sd-text tracking-[-0.02em] leading-tight mb-sd-1">
                Let&rsquo;s run your playbook.
              </h2>
              <p className="sd-reveal-2 text-sd-sm text-sd-text-secondary leading-relaxed mb-sd-5">
                Offload your SOPs. Monitor disputes. Automate reports.
              </p>
              <PlaybookSuggestions
                className="w-full"
                onSelect={(text) => {
                  chatInputRef.current?.setValue(text);
                  chatInputRef.current?.focus();
                }}
              />
            </div>
          </div>
        ) : (
          (() => {
              // Group messages into sections: each section begins at a user message and
              // includes the assistant replies that follow it. Each section wrapper gets
              // `position: relative` so the sticky user message header is bounded within
              // it — this makes incoming headers push outgoing ones up instead of stacking.
              type Msg = typeof messages[0];
              type Section = { userMsg: Msg; following: Msg[] };
              const sections: Section[] = [];
              const leading: Msg[] = [];

              for (const msg of messages) {
                if (msg.role === 'user') {
                  sections.push({ userMsg: msg, following: [] });
                } else if (sections.length === 0) {
                  leading.push(msg);
                } else {
                  sections[sections.length - 1]!.following.push(msg);
                }
              }

              const renderBubble = (msg: Msg) => {
                const hasStreamEvents = msg.role === 'assistant' && msg.streamEvents && msg.streamEvents.length > 0;
                return hasStreamEvents ? (
                  <StreamRenderer
                    events={msg.streamEvents as unknown as StreamEvent[]}
                    {...(conversationId !== null ? { conversationId } : {})}
                    {...(msg.timestamp !== undefined ? { timestamp: msg.timestamp } : {})}
                    onPlanApproved={handlePlanApproved}
                    thinkingCap
                  />
                ) : (
                  <MessageBubble role={msg.role} content={msg.content} {...(msg.timestamp !== undefined ? { timestamp: msg.timestamp } : {})} />
                );
              };

              return (
                <>
                  {leading.map((msg) => (
                    <div key={msg.id}>
                      {renderBubble(msg)}
                      {msg.status === 'error' && (
                        <Button variant="ghost" size="sm" onClick={() => { void retryLastMessage(); }}>Retry</Button>
                      )}
                    </div>
                  ))}
                  {sections.map((section, sectionIndex) => (
                    <div key={section.userMsg.id} style={{ zIndex: 2 + sectionIndex }} className="relative space-y-sd-3">
                      <div className="sticky top-[-12px] bg-sd-background pt-[12px] -mt-[12px]" style={{ zIndex: 1 }}>
                        {renderBubble(section.userMsg)}
                        {section.userMsg.status === 'error' && (
                          <Button variant="ghost" size="sm" onClick={() => { void retryLastMessage(); }}>Retry</Button>
                        )}
                      </div>
                      {section.following.map((msg) => (
                        <div key={msg.id} className="relative" style={{ zIndex: 0 }}>
                          {renderBubble(msg)}
                          {msg.status === 'error' && (
                            <Button variant="ghost" size="sm" onClick={() => { void retryLastMessage(); }}>Retry</Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              );
            })()
        )}
        {isAgentTyping && (
          <div data-testid="typing-indicator" className={`relative flex items-center gap-sd-2 px-sd-3 py-sd-2 rounded-sd-md shadow-sd-sm sd-fade-in w-[85%] ${isExtendedSession ? 'bg-sd-warning/5 border border-sd-warning/20' : 'bg-sd-surface border border-sd-border/20'}`}>
            <div className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full bg-sd-warning/30 animate-pulse" />
            <Spinner size="sm" />
            <span className={`text-sd-sm ${isExtendedSession ? 'text-sd-warning' : 'text-sd-text-muted'}`}>
              {isExtendedSession ? <><span className="text-sd-warning font-medium">shiftagent</span> is working (extended session)&hellip;</> : <><span className="text-sd-warning/70 font-medium">shiftagent</span> is working&hellip;</>}
            </span>
          </div>
        )}
      </div>
      {showNewMessages && (
        <button
          className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-sd-primary text-sd-on-primary px-sd-3 py-sd-1 rounded-full text-sd-sm shadow-sd-md transition-all duration-200 z-10"
          onClick={() => {
            if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
            setShowNewMessages(false);
          }}
        >
          New messages
        </button>
      )}
      <div data-testid="chat-input-footer" className="relative z-20 shrink-0 p-sd-3 bg-sd-surface shadow-[0_-1px_3px_rgba(0,0,0,0.04)]">
        <ChatInput
          ref={chatInputRef}
          onSend={handleSend}
          disabled={isAgentTyping}
          slashCommands={slashCommands}
          slashCommandStatus={slashCommandStatus}
          onSlashCommandsFetch={fetchSlashCommands}
          onSlashCommandsRefresh={fetchSlashCommands}
        />
      </div>
    </div>
  );
}
